import Layout from '../components/Layout';
import SeoHead from '../components/SeoHead';
import { SectionHeader, ServiceCard, ContactCta, FaqAccordion } from '../components/Shared';
import HeroAppsImage from '../components/HeroAppsImage';
import ResponsiveImage, {
  FEATURE_IMAGE_SIZES,
  LOGO_IMAGE_SIZES,
  PORTFOLIO_IMAGE_SIZES,
} from '../components/ResponsiveImage';
import { useCityDetection } from '../hooks/useSiteEffects';
import {
  SOLUTIONS,
  PROCESS_STEPS,
  SERVICE_BLOCKS,
  TESTIMONIALS,
  PORTFOLIO,
  FAQ_ITEMS,
  CITY_LINKS,
} from '../data/siteData';
import { buildFaqJsonLd, buildLocalBusinessJsonLd } from '../data/cityPages';

const CLIENT_LOGOS = [
  { image: '/assets/images/mcl.webp', name: 'MCL Peças Agrícolas', city: 'Palotina', width: 1024, height: 1024 },
  { image: '/assets/images/friolog.png', name: 'FRIOLOG', city: 'Maripá', width: 1024, height: 1024 },
  { image: '/assets/images/demac.png', name: 'Demac Peças Agrícolas', city: 'Palotina', width: 1024, height: 1024 },
  { image: '/assets/images/terra.webp', name: 'Colégio Terra do Saber', city: 'Palotina', width: 1024, height: 1024 },
  { image: '/assets/images/fabrica.png', name: 'Fábrica de Águias', city: 'São Paulo', width: 1024, height: 1024 },
  { image: '/assets/images/agromak.png', name: 'Agromak', city: 'Jandaia do Sul', width: 1024, height: 1024 },
  { image: '/assets/images/inova.png', name: 'Instituto Inova', city: 'Palotina', width: 1024, height: 1024 },
];

export default function HomePage() {
  const citySuffix = useCityDetection();

  return (
    <Layout
      title="(in)nova now | Apps e Sistemas Personalizados em Palotina"
      description="A (in)nova now desenvolve aplicativos Android, iOS e sistemas web sob medida em Palotina-PR. Integração com ERP, CMMS, força de vendas e automação industrial."
    >
      <SeoHead
        path="/"
        title="(in)nova now | Apps e Sistemas Personalizados em Palotina"
        description="A (in)nova now desenvolve aplicativos Android, iOS e sistemas web sob medida em Palotina-PR. Integração com ERP, CMMS, força de vendas e automação industrial."
        keywords="desenvolvimento de sistema palotina, desenvolvimento de sistemas palotina, apps personalizados palotina, software sob medida palotina, innovanow"
        jsonLd={[buildLocalBusinessJsonLd(), buildFaqJsonLd(FAQ_ITEMS)]}
      />
      {/* Hero */}
      <section id="sobre" className="in-section in-section--hero">
        <div className="in-container">
          <div className="in-hero__grid">
            <div data-aos="fade-right">
              <span className="in-section__eyebrow">
                <i className="ri-sparkling-2-line" /> Software sob medida
              </span>
              <h1 className="in-section__title in-section__title--gradient">
                Apps e sistemas que transformam a operação da sua empresa
              </h1>
              <p className="in-hero__lead">
                Da força de vendas à automação industrial — criamos software integrado ao seu ERP para aumentar
                produtividade e reduzir custos operacionais.
              </p>
              <div className="in-hero__actions">
                <a
                  href="https://wa.me/5544991775641"
                  className="in-btn in-btn--primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Solicitar orçamento
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </a>
                <a href="#solucoes" className="in-btn in-btn--ghost">
                  Ver soluções
                </a>
              </div>
              <div className="in-hero__stats">
                <div className="in-stat">
                  <div className="in-stat__value">15+</div>
                  <div className="in-stat__label">Soluções publicadas</div>
                </div>
                <div className="in-stat">
                  <div className="in-stat__value">100%</div>
                  <div className="in-stat__label">Projetos personalizados</div>
                </div>
                <div className="in-stat">
                  <div className="in-stat__value">ERP</div>
                  <div className="in-stat__label">Integração nativa</div>
                </div>
              </div>
            </div>
            <div className="in-hero__visual" data-aos="fade-left">
              <div className="in-hero__card">
                <HeroAppsImage priority />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Soluções */}
      <section id="solucoes" className="in-section">
        <div className="in-container">
          <SectionHeader
            eyebrow="Soluções"
            title="O que desenvolvemos para sua empresa"
            description="Plataformas web e apps na Google Play e App Store — cada solução integrada aos processos do seu negócio."
          >
            <p style={{ marginTop: 16 }}>
              <a
                href="https://play.google.com/store/apps/developer?id=innova+now"
                className="in-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Ver todos na Google Play →
              </a>
            </p>
          </SectionHeader>
          <div className="in-grid in-grid--3">
            {SOLUTIONS.map((item, index) => (
              <ServiceCard key={item.title} {...item} delay={(index % 3) * 80} />
            ))}
          </div>
        </div>
      </section>

      {/* Processo */}
      <section id="processo" className="in-section">
        <div className="in-container">
          <SectionHeader
            eyebrow="Processo"
            title="Como trabalhamos"
            description="Da primeira conversa ao suporte contínuo — transparente em cada etapa."
          />
          <div className="in-process" data-aos="fade-up">
            {PROCESS_STEPS.map((step) => (
              <div key={step.number} className="in-process__step">
                <div className="in-process__num">{step.number}</div>
                <h3 className="in-process__title">{step.title}</h3>
                <p className="in-process__text">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section id="servicos" className="in-section">
        <div className="in-container">
          {SERVICE_BLOCKS.map((block, index) => (
            <div
              key={block.title}
              className={'in-feature' + (block.imageFirst ? ' in-feature--reverse' : '')}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="in-feature__content">
                <span className="in-section__eyebrow">{block.badge}</span>
                <h2 className="in-section__title" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                  {block.title}
                </h2>
                <p className="in-section__desc">{block.description}</p>
                <ul className="in-feature__list">
                  {block.features.map((f) => (
                    <li key={f}>
                      <i className="ri-check-line" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="in-feature__media">
                <ResponsiveImage
                  src={block.image}
                  alt={block.imageAlt}
                  width={block.imageWidth}
                  height={block.imageHeight}
                  sizes={FEATURE_IMAGE_SIZES}
                  widths={[600, 1200]}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Clientes */}
      <section id="clientes" className="in-section">
        <div className="in-container">
          <SectionHeader
            eyebrow="Clientes"
            title="Quem confia na (in)nova now"
            description="Empresas de agronegócio, indústria e educação que automatizaram processos conosco."
          />
          <div className="in-logos" data-aos="fade-up">
            {CLIENT_LOGOS.map((client) => (
              <div key={client.name} className="in-logo-item">
                <ResponsiveImage
                  src={client.image}
                  alt={client.name}
                  width={client.width}
                  height={client.height}
                  sizes={LOGO_IMAGE_SIZES}
                  widths={[96, 192]}
                />
                <strong style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--in-text)', marginBottom: 4 }}>
                  {client.name}
                </strong>
                <span>{client.city}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section id="depoimentos" className="in-section">
        <div className="in-container">
          <SectionHeader
            eyebrow="Resultados"
            title="O que nossos clientes conquistam"
            description="Impacto real em operações de campo, manutenção e vendas."
          />
          <div className="in-grid in-grid--3">
            {TESTIMONIALS.map((item, index) => (
              <div key={item.name} className="in-quote" data-aos="fade-up" data-aos-delay={index * 100}>
                <blockquote>{item.quote}</blockquote>
                <div className="in-quote__author">
                  <ResponsiveImage
                    src={item.image}
                    alt={item.name}
                    width={item.imageWidth}
                    height={item.imageHeight}
                    sizes={LOGO_IMAGE_SIZES}
                    widths={[96, 192]}
                  />
                  <div>
                    <strong>{item.name}</strong>
                    <small>{item.role}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfólio */}
      <section id="apps" className="in-section">
        <div className="in-container">
          <SectionHeader eyebrow="Portfólio" title="Projetos em destaque" />
          <div className="in-portfolio">
            {PORTFOLIO.map((item, index) => (
              <article key={item.title} className="in-portfolio__item" data-aos="fade-up" data-aos-delay={(index % 3) * 100}>
                <ResponsiveImage
                  src={item.image}
                  alt={item.title}
                  width={item.imageWidth}
                  height={item.imageHeight}
                  sizes={PORTFOLIO_IMAGE_SIZES}
                  widths={[480, 960]}
                />
                <div className="in-portfolio__overlay">
                  <span className="in-portfolio__result">{item.result}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <a
                    href={item.href}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                  >
                    Saiba mais →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Store badges */}
      <section className="in-section" style={{ paddingTop: 0 }}>
        <div className="in-container">
          <SectionHeader eyebrow="Lojas" title="Baixe nossos apps" />
          <div className="in-badges" data-aos="fade-up">
            <a href="https://play.google.com/store/apps/developer?id=innova+now" target="_blank" rel="noopener noreferrer">
              <img
                src="/assets/images/playstore.png"
                alt="Google Play"
                width={646}
                height={250}
                loading="lazy"
                decoding="async"
              />
            </a>
            <a href="https://apps.apple.com/br/developer/in-nova-now-tech/id1790867569" target="_blank" rel="noopener noreferrer">
              <img
                src="/assets/images/apple.svg"
                alt="App Store"
                width={120}
                height={40}
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Cidades */}
      <section id="atendimento" className="in-section">
        <div className="in-container">
          <SectionHeader
            eyebrow="Atendimento"
            title="Empresas em todo o Paraná e Brasil"
            description="Sede em Palotina-PR, com projetos entregues em diversas cidades."
          />
          <div className="in-tags" data-aos="fade-up">
            {CITY_LINKS.map((city) => (
              <a key={city.href} href={city.href} className="in-tag">
                {city.label}
              </a>
            ))}
          </div>
          <p className="in-note">
            Não encontrou sua cidade?{' '}
            <a href="https://wa.me/5544991775641">Fale conosco</a> — atendemos todo o Brasil.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="in-section">
        <div className="in-container">
          <SectionHeader eyebrow="Dúvidas" title="Perguntas frequentes" />
          <FaqAccordion items={FAQ_ITEMS} />
        </div>
      </section>

      <ContactCta title="Tem algum processo que pode ser automatizado" citySuffix={citySuffix} />
    </Layout>
  );
}
